import { _ } from "../../polyglot/polyglot.js";
import {
	allCompanyRoles,
	companyNormalRoleName,
	getPresentationName,
} from "../../../models/CompanyUserRoles.js";
import { notBlank, validEmail } from "../forms/validators.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../list/streams/ArrayStream.js";
import ListSource from "../../list/ListSource.js";
import makeCardListWidget from "../list/CardListWidget/makeCardListWidget.js";
import removeUserDialog from "../removeUserDialog.js";
import userAvatarWidget from "../userAvatarWidget.js";
import userInfoDialog from "../userInfoDialog.js";

/**
 * Displays a grid of user avatars excluding the current user.
 *
 * @param {string} [addIconLabel] - Text to display under the "plus" button to invite user. Default is "Invite user"
 * @param {user} [currentUser] - User to exclude from the list
 * @param {company} [company] - Company to which the users belong
 * @param {string} [dataTestName] - String used as data-test attribute
 *   to facilitate UI testing
 */
export default class UsersWidget extends Widget {
	constructor({ addIconLabel, currentUser, company, dataTestName } = {}) {
		super(...arguments);
		this._addIconLabel = addIconLabel || _("Invite user");
		this._currentUser = currentUser;
		this._company = company;
		this._users = null;

		this._createEvents("onRemoveUser", "onCreateUser");
		this._dataTestName = dataTestName;

		this._cardList = makeCardListWidget({
			itemRenderer: this._cardRenderer.bind(this),
			trailingWidget: this._renderTrailingCardOn.bind(this),
		});
	}

	setUsers(listOfUsers) {
		this._users = listOfUsers;

		this._cardList.setSource(
			ListSource.makeAndFetch({
				stream: new ArrayStream({ data: listOfUsers }),
			}),
		);

		this.update();
	}

	renderContentOn(html) {
		html.div(
			{
				klass: "section width-full pull-left slideover-parent display-user-cards",
				"data-test": this._dataTestName,
			},
			this._cardList,
		);
	}

	_showInviteUserDialog() {
		this._configure({
			title: _("Invite user"),
			okLabel: _("Invite"),
			formSpec: {
				model: {
					companyRole: companyNormalRoleName,
				},
				saveAction: (userToCreate) => {
					this.onCreateUser.trigger(userToCreate);
				},
			},
			formBuilder: (form) => {
				form.newDropDownField({
					name: "company-roles",
					label: _("Company role"),
					attribute: "companyRole",
					items: allCompanyRoles().map((roleName) => ({
						label: getPresentationName(roleName),
						value: roleName,
						selected: roleName === companyNormalRoleName,
					})),
				});

				form.input({
					placeHolder: _("Name"),
					label: _("Name"),
					attribute: "name",
					validator: notBlank(),
				});

				form.input({
					placeHolder: _("Surname"),
					label: _("Surname"),
					attribute: "surname",
					validator: notBlank(),
				});

				form.input({
					placeHolder: _("Email"),
					label: _("Email"),
					attribute: "email",
					validator: validEmail(),
				});
			},
		});
	}

	_cardRenderer(user) {
		return (html) => {
			const avatarWidget = userAvatarWidget({ user });

			html.div(avatarWidget);

			this._registerCardEvents(avatarWidget, user);
		};
	}

	_registerCardEvents(card, user) {
		const dialog = userInfoDialog({
			company: this._company,
			user,
			canRemove:
				!this._currentUser ||
				user.getId() !== this._currentUser.getId(),
		});

		dialog.onRemoveUser.register(() => {
			this._showRemoveUserDialog(user);
		});

		card.onClick.register(dialog.show);
	}

	_renderTrailingCardOn(html) {
		let avatar;

		html.div(
			{
				class: "avatar-icon",
			},
			html.div(
				{ class: "consultant admin-comp-popover avatar" },
				(html) => {
					avatar = html.div({
						"data-test": "invite-user",
						class: "add-new-popover add-new img-circle",
					});
				},
				html.p(this._addIconLabel),
			),
		);

		avatar.click(() => this._showInviteUserDialog());
	}

	_showRemoveUserDialog(user) {
		const dialog = removeUserDialog({ user });
		dialog.onRemoveUser(this.onRemoveUser.trigger);
		dialog.show();
	}
}
